const dateFieldRegistry = {}
const dateFieldListeners = []

function isDateOnlyString (value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
}

function getSystemTimezone () {
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) {
    return 'UTC'
  }

  const options = Intl.DateTimeFormat().resolvedOptions()
  return options && options.timeZone ? options.timeZone : 'UTC'
}

function getDateParts (date, timezone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })

  return formatter.formatToParts(date).reduce((result, part) => {
    if (part.type !== 'literal') {
      result[part.type] = Number(part.value)
    }
    return result
  }, {})
}

function padDatePart (value) {
  return value < 10 ? `0${value}` : `${value}`
}

function formatDateInTimezone (date, timezone) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null
  }

  const parts = getDateParts(date, timezone)
  return `${parts.year}-${padDatePart(parts.month)}-${padDatePart(parts.day)}`
}

function getTimezoneOffset (date, timezone) {
  const parts = getDateParts(date, timezone)
  const hour = parts.hour === 24 ? 0 : parts.hour
  const dateAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, hour, parts.minute, parts.second)
  return dateAsUtc - date.getTime()
}

function dateInputToDate (value, timezone) {
  if (!isDateOnlyString(value)) {
    return null
  }

  const parts = value.split('-').map(Number)
  const utcMidnight = Date.UTC(parts[0], parts[1] - 1, parts[2], 0, 0, 0)
  let offset = getTimezoneOffset(new Date(utcMidnight), timezone)
  let date = new Date(utcMidnight - offset)
  const adjustedOffset = getTimezoneOffset(date, timezone)

  if (adjustedOffset !== offset) {
    date = new Date(utcMidnight - adjustedOffset)
  }

  return date
}

function normalizeDateInput (value, timezone) {
  if (!value) {
    return null
  }

  if (isDateOnlyString(value)) {
    return value
  }

  const date = value instanceof Date ? value : new Date(value)
  return formatDateInTimezone(date, timezone)
}

function setRegistryValue (id, value) {
  if (!id) {
    return
  }

  dateFieldRegistry[id] = value || null
  dateFieldListeners.forEach(listener => listener(id, dateFieldRegistry[id]))
}

function addRegistryListener (listener) {
  dateFieldListeners.push(listener)

  return function removeRegistryListener () {
    const index = dateFieldListeners.indexOf(listener)
    if (index >= 0) {
      dateFieldListeners.splice(index, 1)
    }
  }
}

export default {
  name: 'st-date-field',
  inheritAttrs: false,
  props: {
    value: [String, Date, Number],
    label: String,
    disabled: Boolean,
    hideDetails: [Boolean, String],
    textFieldProps: {
      type: Object,
      default: () => ({})
    },
    clearable: {
      type: Boolean,
      default: true
    },
    id: String,
    fromDateId: String,
    toDateId: String,
    min: [String, Date, Number],
    max: [String, Date, Number],
    returnDate: Boolean,
    timezone: String
  },
  data () {
    return {
      linkedFromValue: null,
      linkedToValue: null,
      removeRegistryListener: null
    }
  },
  computed: {
    resolvedTimezone () {
      return this.$props.timezone || getSystemTimezone()
    },
    inputValue () {
      return normalizeDateInput(this.$props.value, this.resolvedTimezone)
    },
    computedLabel () {
      return this.$props.label || (this.$props.textFieldProps && this.$props.textFieldProps.label) || null
    },
    fieldProps () {
      return Object.assign({}, this.$attrs || {}, this.$props.textFieldProps || {})
    },
    computedMin () {
      return normalizeDateInput(this.$props.min, this.resolvedTimezone) || this.linkedFromValue || null
    },
    computedMax () {
      return normalizeDateInput(this.$props.max, this.resolvedTimezone) || this.linkedToValue || null
    },
    appendIcon () {
      return this.$props.clearable && this.inputValue && !this.$props.disabled ? 'fal fa-times' : ''
    },
    forwardedListeners () {
      return Object.keys(this.$listeners || {}).reduce((result, key) => {
        if (key !== 'input' && key !== 'change' && key !== 'clear') {
          result[key] = this.$listeners[key]
        }

        return result
      }, {})
    }
  },
  watch: {
    value () {
      this.updateRegisteredValue()
    },
    timezone () {
      this.updateRegisteredValue()
    },
    id () {
      this.updateRegisteredValue()
    },
    fromDateId () {
      this.refreshLinkedValues()
    },
    toDateId () {
      this.refreshLinkedValues()
    }
  },
  mounted () {
    this.removeRegistryListener = addRegistryListener(this.onRegistryValueChange)
    this.refreshLinkedValues()
    this.updateRegisteredValue()
  },
  beforeDestroy () {
    if (this.removeRegistryListener) {
      this.removeRegistryListener()
    }

    if (this.$props.id && dateFieldRegistry[this.$props.id] === this.inputValue) {
      setRegistryValue(this.$props.id, null)
    }
  },
  methods: {
    toOutputValue (value) {
      if (!value) {
        return null
      }

      return this.$props.returnDate ? dateInputToDate(value, this.resolvedTimezone) : value
    },
    updateRegisteredValue () {
      setRegistryValue(this.$props.id, this.inputValue)
    },
    refreshLinkedValues () {
      this.linkedFromValue = this.$props.fromDateId ? dateFieldRegistry[this.$props.fromDateId] || null : null
      this.linkedToValue = this.$props.toDateId ? dateFieldRegistry[this.$props.toDateId] || null : null
    },
    onRegistryValueChange (id, value) {
      if (id === this.$props.fromDateId) {
        this.linkedFromValue = value || null
      }

      if (id === this.$props.toDateId) {
        this.linkedToValue = value || null
      }
    },
    emitInput (value) {
      const inputValue = value || null
      this.$emit('input', this.toOutputValue(inputValue))
      setRegistryValue(this.$props.id, inputValue)
    },
    onInput (value) {
      this.emitInput(value)
    },
    onChange (value) {
      this.$emit('change', this.toOutputValue(value || null))
    },
    clearValue () {
      this.emitInput(null)
      this.$emit('change', null)
      this.$emit('clear')
    }
  }
}
